import * as Sentry from '@sentry/react';
import promiseFinally from 'promise.prototype.finally';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BrowserRouter, MemoryRouter } from 'react-router-dom';
import { CompatRouter } from 'react-router-dom-v5-compat';

import { logout, submitOpcLogoutFormRequest } from '~/api/auth';
import { getClient } from '~/api/client';
import { getCurrentUser } from '~/api/currentUser';
import AuthenticationStore from '~/auth/authenticationStore';
import { useStytchClient } from '~/auth/useStytchClient';
import { StytchProvider } from '~/components/Stytch/StytchProvider';
import { AppConfigProvider } from '~/contexts/AppConfig';
import { I18nProvider } from '~/contexts/I18nProvider';
import { PusherProvider } from '~/contexts/PusherContext';
import { isDev } from '~/dev/utils/isDev';
import { useDynamicallyLoadMockServiceIfEnabled } from '~/dev/utils/MockService/useDynamicallyLoadMockServiceIfEnabled';
import { PanelProvider } from '~/eds/src/components/Panel/PanelContext';
import { initialize as initializeEds } from '~/eds/src/system';
import { QueryParamType } from '~/enums/QueryParamType';
import { FlagType } from '~/flags/flags';
import { useExposeLDClient, useFlag } from '~/flags/useFlag';
import { useFlagsReady } from '~/flags/useFlagsReady';
import { useStaleWhileRevalidate } from '~/hooks/useStaleWhileRevalidate';
import { useAsyncLanguageLoader } from '~/i18n/hooks/useAsyncLanguageLoader';
import { getEnglishMessages } from '~/i18n/loadLocale';
import { slices } from '~/redux/slices';
import {
  getClientFromUrl,
  getRawClientFromUrl,
  shouldSkipSubdomainCheck,
} from '~/routing/utils';
import { MetricsService } from '~/services/MetricsService/MetricsService';
import {
  redirectToOAuth,
  redirectToProviderAuthPage,
  redirectToZendesk,
} from '~/services/redirects';
import { getPageSearchQueryByKey } from '~/utils/searchQuery';

import ErrorBoundary from '../ErrorBoundary/ErrorBoundary';
import SplashPage from '../SplashPage/SplashPage';
import UnsupportedBrowserPage from '../UnsupportedBrowserPage/UnsupportedBrowserPage';
import { Welcome } from '../Welcome/Welcome';
import AppContents from './AppContents/AppContents';

// Needed for environments that do not support .finally() on promises
promiseFinally.shim();

initializeEds();

const USER_STORAGE_KEY = 'currentUser';

const isIE11AndPrior = () => {
  const ua = window.navigator.userAgent;
  const msie = ua.indexOf('MSIE ');
  const trident = ua.indexOf('Trident');
  if (msie > 0) {
    return parseInt(ua.substring(msie + 5, ua.indexOf('.', msie)), 10) <= 10;
  } else if (trident > 0) {
    return true;
  } else {
    return false;
  }
};

const useCachedCurrentUser = ({ currentUser }) => {
  const shouldFetchCurrentUser =
    AuthenticationStore().isLoggedIn() && Object.keys(currentUser).length === 0;

  const { data: user, isLoading } = useStaleWhileRevalidate(
    USER_STORAGE_KEY,
    () => getCurrentUser(),
    {
      skip: !shouldFetchCurrentUser,
      onError: (error) => {
        if (error.response && error.response.status === 423) {
          window.location.href = '/folders-permission-check';
        }
      },
    },
  );

  return { isLoading, user };
};

function App() {
  const dispatch = useDispatch();
  const isMockReady = useDynamicallyLoadMockServiceIfEnabled();

  useExposeLDClient();

  const language = useSelector(slices.i18n.selectors.selectLanguage);
  const userCurrency = useSelector(slices.i18n.selectors.selectCurrency);
  const currentUser = useSelector(
    slices.currentUser.selectors.selectCurrentUser,
  );

  const { origin, pathname } = window.location;
  const { b2bClient } = useStytchClient();
  const isUserLoggedIn = AuthenticationStore().isLoggedIn();
  // TODO: research and untangle and remove the need for this check
  const enableReady = !(
    !pathname.includes('folders-permission-check') &&
    !isDev &&
    isUserLoggedIn
  );
  const [subdomain, setSubdomain] = useState('');
  const isReady = useFlagsReady({ enableReady, user: currentUser });
  const { messages, isLoading: isLocaleLoading } =
    useAsyncLanguageLoader(language);

  const shouldInitialize =
    Object.keys(currentUser).length === 0 && pathname !== '/';

  const { user } = useCachedCurrentUser({ currentUser });

  /* For zendesk authenticate */
  const zendeskQuerySsoFlag = getPageSearchQueryByKey(QueryParamType.Sso);
  const zendeskQuerySsoAction = getPageSearchQueryByKey(QueryParamType.Action);
  const zendeskQueryRedirectUrl = getPageSearchQueryByKey(
    QueryParamType.ReturnTo,
  );
  /* For OAuth authenticate */
  const oAuthQueryClientId = getPageSearchQueryByKey(QueryParamType.ClientId);
  const oAuthQueryResponseType = getPageSearchQueryByKey(
    QueryParamType.ResponseType,
  );
  const oAuthQueryRedirectUri = getPageSearchQueryByKey(
    QueryParamType.RedirectUri,
  );
  const oAuthQueryState = getPageSearchQueryByKey(QueryParamType.State);
  /* For 3rd-party provider auth */
  const providerAuthState = getPageSearchQueryByKey(QueryParamType.State);
  const providerAuthCode = getPageSearchQueryByKey(QueryParamType.Code);

  const isComponentMonitoringEnabled = useFlag(
    FlagType.ComponentMonitoringInSentry,
  );

  useEffect(() => {
    if (isUserLoggedIn) {
      MetricsService.appMounted(user);
    }
  }, [isUserLoggedIn]);

  useEffect(() => {
    if (user) {
      const { client_config } = user;
      const { id: clientId, sub_domain } = client_config;

      void checkSubDomain(sub_domain);

      dispatch(slices.currentUser.actions.setCurrentUser(user));
      dispatch(slices.i18n.actions.setLanguage(user.language ?? 'en'));
      dispatch(slices.i18n.actions.setLocale(user.locale ?? 'system-default'));
      Sentry.setUser({ email: user.email, id: user.id });

      if (sub_domain && !subdomain) {
        setSubdomain(sub_domain.toLowerCase());
      }

      getClient(clientId).then((client) =>
        dispatch(slices.client.actions.clientSet(client)),
      );
      AuthenticationStore().saveSubDomainInSession(sub_domain);
    }
  }, [user]);

  const checkSubDomain = async (userSubdomain) => {
    if (shouldSkipSubdomainCheck(pathname)) {
      return;
    }

    const clientSubdomain = getClientFromUrl();

    const isPublicError = ['word-online-invalid-subscription'].includes(
      clientSubdomain,
    );

    if (
      !isPublicError &&
      userSubdomain &&
      clientSubdomain &&
      clientSubdomain !== userSubdomain
    ) {
      try {
        const response = await logout();
        if (
          response?.workday_logout_url &&
          response?.id_token_hint &&
          response?.oidc_client_id
        ) {
          submitOpcLogoutFormRequest({
            action: response.workday_logout_url,
            id_token_hint: response.id_token_hint,
            oidc_client_id: response.oidc_client_id,
          });

          return; // Early return - form submission will navigate away
        }
      } catch (error) {
        // logout() failed, but we still want to do the fallback redirect
        console.error('Logout failed:', error);
      }

      // Fallback redirect logic (only runs if no redirect_url or logout failed)
      const redirectSubdomainContainPublicPath = [
        'activation',
        'change-password',
        'expired-token',
        'external-document-viewer',
        'forgot-password',
      ].includes(clientSubdomain);

      AuthenticationStore().clearAuthentication();

      if (redirectSubdomainContainPublicPath) {
        window.location.href = `${origin}/${clientSubdomain}${window.location.search}`;
      } else {
        window.location.href = `${origin}/${clientSubdomain}/login`;
      }
    }
  };

  const checkRedirectToZendesk = (loginStatus) => {
    const zendeskRedirectUrl = AuthenticationStore().getZendeskUrl();
    const zendeskRedirectExp = AuthenticationStore().getZendeskExpire();

    if (!zendeskRedirectUrl && zendeskQuerySsoFlag === 'zendesk') {
      AuthenticationStore().setZendeskUrl(zendeskQueryRedirectUrl);
    }

    if (
      zendeskQuerySsoFlag === 'zendesk' &&
      zendeskQuerySsoAction === 'logout'
    ) {
      AuthenticationStore().removeZendeskFlag();
      AuthenticationStore().clearAuthentication();
      window.location.href = `${origin}`;
    }

    if (zendeskRedirectUrl && loginStatus) {
      if (parseInt(zendeskRedirectExp, 10) < new Date().valueOf()) {
        AuthenticationStore().removeZendeskFlag();
      } else {
        redirectToZendesk(zendeskRedirectUrl);
      }
    }
  };

  const checkRedirectToOAuth = (loginStatus) => {
    const [oAuthRedirectUri, oAuthClientId, oAuthState] =
      AuthenticationStore().getOAuthVars();
    const oAuthRedirectExp = AuthenticationStore().getOAuthExpire();

    if (!oAuthRedirectUri && oAuthQueryResponseType === 'code') {
      AuthenticationStore().setOAuthVars(
        oAuthQueryRedirectUri,
        oAuthQueryClientId,
        oAuthQueryState,
      );
    }

    if (oAuthRedirectUri && loginStatus) {
      if (parseInt(oAuthRedirectExp, 10) < new Date().valueOf()) {
        AuthenticationStore().removeOAuthFlag();
      } else {
        redirectToOAuth(oAuthRedirectUri, oAuthClientId, oAuthState);
      }
    }
  };

  const fetchThirdPartyProviderAuthStateData = (state, code) => {
    redirectToProviderAuthPage(state, code);
  };

  useEffect(() => {
    if (pathname.includes('folders-permission-check')) return;

    if (isUserLoggedIn && pathname.includes('evisync') && providerAuthState) {
      return fetchThirdPartyProviderAuthStateData(
        providerAuthState,
        providerAuthCode,
      );
    }

    checkRedirectToZendesk(isUserLoggedIn);
    checkRedirectToOAuth(isUserLoggedIn);

    // TODO: clean up this condition
    if (!(isUserLoggedIn && Object.keys(currentUser).length === 0)) {
      if (pathname !== '/') {
        setSubdomain(getClientFromUrl());
      }
    }
  }, [isUserLoggedIn, pathname, currentUser]);

  const rawSubdomain = getRawClientFromUrl();

  if (
    !isMockReady ||
    !isReady ||
    isLocaleLoading ||
    (!subdomain &&
      !pathname.includes('folders-permission-check') &&
      rawSubdomain &&
      (isUserLoggedIn || shouldInitialize))
  ) {
    return (
      <I18nProvider
        currency={userCurrency}
        language={language}
        messages={getEnglishMessages()}
      >
        <SplashPage />
      </I18nProvider>
    );
  }

  if (isIE11AndPrior()) {
    return <UnsupportedBrowserPage />;
  }

  const Router = window.require ? MemoryRouter : BrowserRouter;

  return (
    <I18nProvider
      currency={userCurrency}
      language={language}
      messages={messages}
    >
      <Router basename={`/${rawSubdomain}`}>
        <CompatRouter>
          <Sentry.ErrorBoundary fallback={ErrorBoundary}>
            <AppConfigProvider>
              <StytchProvider stytch={b2bClient}>
                <PusherProvider>
                  <PanelProvider>
                    <Sentry.Profiler
                      name="AppContents"
                      disabled={!isComponentMonitoringEnabled}
                    >
                      <AppContents />
                    </Sentry.Profiler>
                    <Welcome />
                  </PanelProvider>
                </PusherProvider>
              </StytchProvider>
            </AppConfigProvider>
          </Sentry.ErrorBoundary>
        </CompatRouter>
      </Router>
    </I18nProvider>
  );
}

export default App;
